/*
 * Admin "open as user" sessions.
 *
 * The app keeps its session in localStorage (token, myEmail, expiresAt) and
 * in the redux-persist blob (persist:root), both shared by every tab of the
 * origin. An admin viewing the app as another user needs a session that
 * lives in ONE tab only, so their own login keeps working next door.
 *
 * The overlay below patches Storage.prototype for window.localStorage in
 * this tab: reads and writes of the session keys go to a per-tab store in
 * sessionStorage instead. Everything else in the app (42 direct token
 * reads, redux-persist, the auth sync hook, logout) works unchanged.
 */

const SESSION_KEY = "cache.impersonation";
const OVERLAY_PREFIX = "cache.impersonation.ls.";
// Keys that belong to the signed-in session (or name a selected profile).
const OVERLAY_KEYS = new Set([
  "token", "myEmail", "expiresAt", "permissions", "athleteEmail", "coachEmail",
  "persist:root", "athleteUpgradeSeen",
]);

const isOverlayKey = (key) => OVERLAY_KEYS.has(key) || String(key).startsWith("persist:");

export const readImpersonation = () => {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

export const isImpersonating = () => !!readImpersonation();

let installed = false;

// Route localStorage session keys to a per-tab store while impersonating.
export const installImpersonationOverlay = () => {
  if (installed) return true;
  const session = readImpersonation();
  if (!session) return false;
  try {
    const proto = Object.getPrototypeOf(window.localStorage);
    const original = {
      getItem: proto.getItem,
      setItem: proto.setItem,
      removeItem: proto.removeItem,
    };
    const isLocal = (store) => store === window.localStorage;
    proto.getItem = function (key) {
      if (isLocal(this) && isOverlayKey(key)) {
        return original.getItem.call(window.sessionStorage, OVERLAY_PREFIX + key);
      }
      return original.getItem.call(this, key);
    };
    proto.setItem = function (key, value) {
      if (isLocal(this) && isOverlayKey(key)) {
        return original.setItem.call(window.sessionStorage, OVERLAY_PREFIX + key, value);
      }
      return original.setItem.call(this, key, value);
    };
    proto.removeItem = function (key) {
      if (isLocal(this) && isOverlayKey(key)) {
        return original.removeItem.call(window.sessionStorage, OVERLAY_PREFIX + key);
      }
      return original.removeItem.call(this, key);
    };
    // Seed the per-tab session from the impersonation payload the first
    // time; later reloads of this tab keep whatever the app wrote.
    if (!original.getItem.call(window.sessionStorage, OVERLAY_PREFIX + "token")) {
      window.localStorage.setItem("token", session.token);
      window.localStorage.setItem("myEmail", session.email);
      window.localStorage.setItem("expiresAt", String(session.expiresAt));
    }
    installed = true;
    return true;
  } catch (e) {
    console.error("impersonation overlay failed", e);
    return false;
  }
};

// Called on the landing page in the NEW tab: record the session, then
// reload so the overlay installs before the store boots.
export const startImpersonation = ({ access, email, name, permissions, impersonatedBy, expiresInSeconds }) => {
  const payload = {
    token: access,
    email,
    name: name || email,
    permissions: permissions || [],
    adminEmail: impersonatedBy || "",
    expiresAt: Date.now() + (Number(expiresInSeconds) || 4 * 3600) * 1000,
    startedAt: Date.now(),
  };
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(payload));
  Object.keys(sessionStorage)
    .filter((k) => k.startsWith(OVERLAY_PREFIX))
    .forEach((k) => sessionStorage.removeItem(k));
  window.location.replace("/");
};

// Leave the user's session: drop the per-tab store; the tab closes when the
// browser allows it (it was opened by script), otherwise it reloads as the
// admin's own session.
export const stopImpersonation = () => {
  Object.keys(sessionStorage)
    .filter((k) => k === SESSION_KEY || k.startsWith(OVERLAY_PREFIX))
    .forEach((k) => sessionStorage.removeItem(k));
  window.close();
  setTimeout(() => window.location.replace("/users"), 150);
};

// Open a new tab running as `email`. `requestToken` calls the admin
// endpoint and resolves with its payload.
export const openAsUser = async (requestToken) => {
  const popup = window.open("", "_blank");
  try {
    const data = await requestToken();
    const params = new URLSearchParams({
      access: data.access, email: data.email, name: data.name || "", permissions: (data.permissions || []).join(","),
      impersonatedBy: data.impersonatedBy || "", expiresInSeconds: String(data.expiresInSeconds || ""),
    });
    const url = `/admin/impersonate#${params.toString()}`;
    if (popup) {
      popup.location.href = url;
    } else {
      window.open(url, "_blank");
    }
    return data;
  } catch (err) {
    if (popup) popup.close();
    throw err;
  }
};
